/**
 * WebAuthn Browser Compatibility Utility
 * Detects WebAuthn API availability and platform authenticator support
 *
 * Sprint 2: PA2-6 - Browser Compatibility Detection
 * Created: February 6, 2026
 */

import React from "react";
import logger from "@shared/utils/logger";

export interface WebAuthnSupport {
  isSupported: boolean;
  hasConditionalMediation: boolean;
  hasPlatformAuthenticator: boolean;
  errorMessage?: string;
  browserName?: string;
  recommendations?: string[];
}

/**
 * Check if WebAuthn is supported in the current browser
 */
export async function checkWebAuthnSupport(): Promise<WebAuthnSupport> {
  const result: WebAuthnSupport = {
    isSupported: false,
    hasConditionalMediation: false,
    hasPlatformAuthenticator: false,
  };

  // Check basic WebAuthn API availability
  if (!window.PublicKeyCredential) {
    result.errorMessage = "WebAuthn is not supported in this browser";
    result.recommendations = [
      "Please upgrade to a modern browser:",
      "• Chrome 67+ or Edge 18+",
      "• Firefox 60+",
      "• Safari 13+",
    ];
    return result;
  }

  result.isSupported = true;

  // Detect browser
  const userAgent = navigator.userAgent;
  if (userAgent.includes("Chrome/")) {
    result.browserName = "Chrome";
  } else if (userAgent.includes("Firefox/")) {
    result.browserName = "Firefox";
  } else if (userAgent.includes("Safari/") && !userAgent.includes("Chrome")) {
    result.browserName = "Safari";
  } else if (userAgent.includes("Edg/")) {
    result.browserName = "Edge";
  } else {
    result.browserName = "Unknown";
  }

  // Check for conditional mediation (autofill UI)
  try {
    if (
      "isConditionalMediationAvailable" in window.PublicKeyCredential &&
      typeof window.PublicKeyCredential.isConditionalMediationAvailable ===
        "function"
    ) {
      result.hasConditionalMediation =
        await window.PublicKeyCredential.isConditionalMediationAvailable();
    }
  } catch (error) {
    logger.debug("Conditional mediation check failed:", error);
  }

  // Check for platform authenticator (Face ID, Touch ID, Windows Hello)
  try {
    if (
      "isUserVerifyingPlatformAuthenticatorAvailable" in
        window.PublicKeyCredential &&
      typeof window.PublicKeyCredential
        .isUserVerifyingPlatformAuthenticatorAvailable === "function"
    ) {
      result.hasPlatformAuthenticator =
        await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    }
  } catch (error) {
    logger.debug("Platform authenticator check failed:", error);
  }

  return result;
}

/**
 * Get user-friendly message about WebAuthn support
 */
export function getWebAuthnSupportMessage(support: WebAuthnSupport): string {
  if (!support.isSupported) {
    return support.errorMessage || "WebAuthn is not supported";
  }

  const features: string[] = [];

  if (support.hasPlatformAuthenticator) {
    features.push(
      "Built-in biometric authentication (Face ID, Touch ID, Windows Hello)",
    );
  }

  if (support.hasConditionalMediation) {
    features.push("Autofill passkey suggestions");
  }

  if (features.length === 0) {
    return "Passkeys are supported (security keys like YubiKey)";
  }

  return `Passkeys are fully supported with: ${features.join(", ")}`;
}

/**
 * Check if the browser meets minimum requirements for passkeys
 */
export function meetsMinimumRequirements(support: WebAuthnSupport): boolean {
  return support.isSupported && (support.hasPlatformAuthenticator || true); // Allow security keys as fallback
}

/**
 * React hook for WebAuthn support detection
 */
export function useWebAuthnSupport() {
  const [support, setSupport] = React.useState<WebAuthnSupport | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    checkWebAuthnSupport()
      .then(setSupport)
      .catch((error) => {
        logger.error("WebAuthn support check failed:", error);

        setSupport({
          isSupported: false,
          hasConditionalMediation: false,
          hasPlatformAuthenticator: false,
          errorMessage: "Failed to check WebAuthn support",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  return { support, loading };
}
